<template>
  <PageSideNav
    title="Summary & Details"
    v-model:activeItem="activeItem"
    :navigationItems="navigationItems"
    :initiallyOpenGroups="initialOpenGroups"
    @itemClick="handleNavigation"
  />
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { useRouter, useRoute } from 'vue-router';
  import PageSideNav from '../shared/PageSideNav.vue';

  const router = useRouter();
  const route = useRoute();

  const activeItem = ref('alignment-summary');

  // Update active item when the route changes (e.g., clicking a grade)
  watch(
    () => [route.params.grade, route.params.gateway],
    ([newGrade, newGateway]) => {
      if (
        route.name === 'report-gateway' &&
        newGateway &&
        typeof newGateway === 'string'
      ) {
        activeItem.value = `gateway-${newGateway.split('-')[1]}-overview`;
      } else if (
        route.name === 'report-grade-overview' &&
        newGrade &&
        typeof newGrade === 'string'
      ) {
        activeItem.value = `grade-${newGrade.toLowerCase()}`;
      }
    },
    { immediate: true },
  );

  // Keep relevant groups open by default or based on active item
  const initialOpenGroups = computed(() => {
    if (activeItem.value.startsWith('grade-')) {
      return ['reports-by-grade'];
    }
    if (activeItem.value.startsWith('gateway-')) {
      const gatewayNumber = activeItem.value.split('-')[1];
      return [`gateway-${gatewayNumber}`, 'summary-details'];
    }
    // Default open groups based on the visual structure
    return [
      'summary-details',
      'gateway-one',
      'criterion-one-one',
      'reports-by-grade',
      'downloads',
    ];
  });

  // Define the navigation structure
  const navigationItems = [
    {
      title: 'Alignment Summary',
      value: 'alignment-summary',
      // TODO: Add route or scroll logic
    },
    {
      title: 'Publication Details',
      value: 'publication-details',
      // TODO: Add route or scroll logic
    },

    {
      title: 'Gateway 1',
      value: 'gateway-one',
      isH4: true,
      isGroup: true,
      borderTop: true,
      route: {
        name: 'report-gateway',
        params: {
          id: String(route.params.id),
          grade: String(route.params.grade),
          gateway: 'gateway-one',
        },
      },
      children: [
        {
          title: 'Overview',
          value: 'gateway-one-overview',
          route: {
            name: 'report-gateway',
            params: {
              id: String(route.params.id),
              grade: String(route.params.grade),
              gateway: 'gateway-one',
            },
          },
        },
        {
          title: 'Criterion 1.1: Text Quality and Complexity',
          value: 'criterion-one-one',
          borderBottom: true,
          isGroup: true,
          indentChildren: true,
          children: [
            {
              title: 'Indicator 1A',
              value: 'indicator-1a',
              append: '2 / 4',
              appendClasses: 'font-weight-bold text-status-orange',
            },
            {
              title: 'Indicator 1B',
              value: 'indicator-1b',
              append: '4 / 4',
              appendClasses: 'font-weight-bold text-status-green',
            },
            {
              title: 'Indicator 1C',
              value: 'indicator-1c',
              append: '4 / 4',
              appendClasses: 'font-weight-bold text-status-green',
            },
            {
              title: 'Indicator 1D',
              value: 'indicator-1d',
              append: '0 / 4',
              appendClasses: 'font-weight-bold text-status-red',
            },
            {
              title: 'Indicator 1E',
              value: 'indicator-1e',
              append: '2 / 4',
              appendClasses: 'font-weight-bold text-status-orange',
            },
          ],
        },
        {
          title:
            'Criterion 1.2: Alignment to the Standards with Tasks and Questions Grounded in Evidence',
          value: 'criterion-one-two',
          isGroup: true,
          indentChildren: true,
          children: [
            {
              title: 'Indicator 1F',
              value: 'indicator-1f',
              append: '2 / 4',
              appendClasses: 'font-weight-bold text-status-orange',
            },
            {
              title: 'Indicator 1G',
              value: 'indicator-1g',
              append: '4 / 4',
              appendClasses: 'font-weight-bold text-status-green',
            },
            {
              title: 'Indicator 1H',
              value: 'indicator-1h',
              append: '4 / 4',
              appendClasses: 'font-weight-bold text-status-green',
            },
            {
              title: 'Indicator 1I',
              value: 'indicator-1i',
              append: '0 / 4',
              appendClasses: 'font-weight-bold text-status-red',
            },
            {
              title: 'Indicator 1J',
              value: 'indicator-1j',
              append: '2 / 4',
              appendClasses: 'font-weight-bold text-status-orange',
            },
            {
              title: 'Indicator 1K',
              value: 'indicator-1k',
              append: '2 / 4',
              appendClasses: 'font-weight-bold text-status-orange',
            },
            {
              title: 'Indicator 1L',
              value: 'indicator-1l',
              append: '2 / 4',
              appendClasses: 'font-weight-bold text-status-orange',
            },
            {
              title: 'Indicator 1M',
              value: 'indicator-1m',
              append: '2 / 4',
              appendClasses: 'font-weight-bold text-status-orange',
            },
          ],
        },
        {
          title:
            'Criterion 1.3: Tasks and Questions: Foundational Skills Development',
          value: 'criterion-one-three',
          isGroup: true,
          indentChildren: true,
          borderTop: true,
          children: [
            {
              title: 'Indicator 1A',
              value: 'indicator-1a',
              append: '2 / 4',
              appendClasses: 'font-weight-bold text-status-orange',
            },
            {
              title: 'Indicator 1B',
              value: 'indicator-1b',
              append: '4 / 4',
              appendClasses: 'font-weight-bold text-status-green',
            },
            {
              title: 'Indicator 1C',
              value: 'indicator-1c',
              append: '4 / 4',
              appendClasses: 'font-weight-bold text-status-green',
            },
            {
              title: 'Indicator 1D',
              value: 'indicator-1d',
              append: '0 / 4',
              appendClasses: 'font-weight-bold text-status-red',
            },
            {
              title: 'Indicator 1E',
              value: 'indicator-1e',
              append: '2 / 4',
              appendClasses: 'font-weight-bold text-status-orange',
            },
          ],
        },
      ],
    },
    {
      title: 'Gateway 2',
      value: 'gateway-two',
      isH4: true,
      isGroup: true,
      borderTop: true,
      route: {
        name: 'report-gateway',
        params: {
          id: String(route.params.id),
          grade: String(route.params.grade),
          gateway: 'gateway-two',
        },
      },
      children: [
        {
          title: 'Overview',
          value: 'gateway-two-overview',
          route: {
            name: 'report-gateway',
            params: {
              id: String(route.params.id),
              grade: String(route.params.grade),
              gateway: 'gateway-two',
            },
          },
        },
        {
          title: 'Gateway 2 Children',
          value: 'gateway-two-data',
        },
      ],
    },
    {
      title: 'Gateway 3',
      value: 'gateway-three',
      isH4: true,
      isGroup: true,
      borderTop: true,
      route: {
        name: 'report-gateway',
        params: {
          id: String(route.params.id),
          grade: String(route.params.grade),
          gateway: 'gateway-three',
        },
      },
      children: [
        {
          title: 'Overview',
          value: 'gateway-three-overview',
          route: {
            name: 'report-gateway',
            params: {
              id: String(route.params.id),
              grade: String(route.params.grade),
              gateway: 'gateway-three',
            },
          },
        },
        {
          title: 'Gateway 3 Children',
          value: 'gateway-three-data',
        },
      ],
    },
    {
      title: 'Reports by Grade',
      value: 'reports-by-grade',
      isH4: true,
      isGroup: true,
      borderTop: true,
      borderBottom: true,
      children: [
        {
          title: 'Kindergarten',
          value: 'grade-k',
          route: {
            name: 'report-grade-overview',
            params: { grade: 'kindergarten' },
          },
        },
        {
          title: '1st Grade',
          value: 'grade-1',
          route: {
            name: 'report-grade-overview',
            params: { grade: 'first-grade' },
          },
        },
        {
          title: '2nd Grade',
          value: 'grade-2',
          route: {
            name: 'report-grade-overview',
            params: { grade: 'second-grade' },
          },
        },
        {
          title: '3rd Grade',
          value: 'grade-3',
          route: {
            name: 'report-grade-overview',
            params: { grade: 'third-grade' },
          },
        },
        {
          title: '4th Grade',
          value: 'grade-4',
          route: {
            name: 'report-grade-overview',
            params: { grade: 'fourth-grade' },
          },
        },
        {
          title: '5th Grade',
          value: 'grade-5',
          route: {
            name: 'report-grade-overview',
            params: { grade: 'fifth-grade' },
          },
        },
        {
          title: '6th Grade',
          value: 'grade-6',
          route: {
            name: 'report-grade-overview',
            params: { grade: 'sixth-grade' },
          },
        },
        {
          title: '7th Grade',
          value: 'grade-7',
          route: {
            name: 'report-grade-overview',
            params: { grade: 'seventh-grade' },
          },
        },
      ],
    },
    {
      title: 'Downloads',
      value: 'downloads',
      isH4: true,
      isGroup: true,
      children: [
        {
          title: 'Publisher Background',
          value: 'publisher-background',
          titleIcon: 'mdi-tray-arrow-down',
        },
        {
          title: 'Publisher Response',
          value: 'publisher-response',
          titleIcon: 'mdi-tray-arrow-down',
        },
        {
          title: 'Technology Information',
          value: 'technology-information',
          titleIcon: 'mdi-tray-arrow-down',
        },
        {
          title: 'Previous Review Versions',
          value: 'previous-versions',
          isInactive: true,
        },
        {
          title: 'Version 1.0',
          value: 'version-1-0',
          titleIcon: 'mdi-tray-arrow-down',
        },
        {
          title: 'Version 1.3',
          value: 'version-1-3',
          titleIcon: 'mdi-tray-arrow-down',
        },
        {
          title: 'Version 2.0',
          value: 'version-2-0',
          titleIcon: 'mdi-tray-arrow-down',
        },
      ],
    },
  ];

  const handleNavigation = (item: any) => {
    // Update active item on any click
    activeItem.value = item.value;

    // Use vue-router if route info exists
    if (item.route?.name) {
      router.push({
        name: item.route.name,
        params: {
          ...item.route.params,
          id: route.params.id as string, // Pass current report ID
        },
      });
    } else if (item.value) {
      // Placeholder for non-route clicks (e.g., scrolling to a section)
    }
  };
</script>
